import React, { useEffect, useState } from 'react';
import MaterialTable, { MTableToolbar } from '@material-table/core';
import { Box, Paper, Button, Typography } from '@mui/material';
import moment from 'moment';
import { IconSearch, IconArrowNarrowDown, IconDownload  } from '@tabler/icons-react';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import RefreshDashboardColumns from './tenantRefreshDashboardColumn';
import CustomPagination from './components/customPagination';
import CustomHeader from './components/customHeader';
import { store } from 'store/store';
import * as UserTypes from 'features/user/userTypes';
import { DEFAULT_FORMAT } from 'utils/dateUtil';

const TableContainer = (props) => (
  <Paper
    {...props}
    elevation={0}
    sx={{
      '& > .MuiBox-root:nth-of-type(2)': {
        border: '1px solid #71767A',
        borderRadius: '1.5rem',
        boxShadow: 'none',
      },
      '& .MuiFormControl-root': {
        flex: 1,
        paddingLeft: '0 !important',
        border: 'none !important',
      },
      '& .MuiTableRow-root td': {
        borderBottom: '1px solid #71767A'
      },
      '& .MuiTableRow-root:last-child td': {
        borderBottom: 'none'
      },
      '& .MuiTableFooter-root': {
        height: 'auto',
      },
      '& .MuiTableCell-head:hover': {
        backgroundColor: '#EFF5FF !important',
        cursor: 'pointer'
      },
      '& .MuiTableSortLabel-root:hover': {
        color: '#000'
      },
      '& .MuiTableSortLabel-icon': {
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out'
      },
      '& .MuiTableCell-head:hover .MuiTableSortLabel-icon, .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
        opacity: 1             
      }
    }}
  />
);

const handleDownloadCSV = (columns, tableData) => {
  const headers = columns.map(col => col.title).join(',');
  
  const rows = tableData.map(row => 
    columns.map(col => {
      const val = row[col.field];
      if (['dataThruDate', 'scheduledRefreshDate'].includes(col.field)) {
        return col.field === 'scheduledRefreshDate'
          ? DEFAULT_FORMAT(moment(val).add(3, 'days'))
          : DEFAULT_FORMAT(val);
      }
      if (col.field === 'refreshProgress') {
        return `${row.stagedFiles} of ${row.totalFiles}`;
      }
      if (col.field === 'refreshStatus') {
        if (['Very Late', 'Late'].includes(val)) {
          return 'Late';
        } 
        if (['Queued', 'Running'].includes(val)) {
          return 'Processing';
        } 
        return 'Current'; 
      }
      if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    }).join(',')
  );
  const csvContent = [headers, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const currentDate = moment().format('MM-DD-YYYY');
  link.download = `refreshstatus_${currentDate}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const TableToolBar = (props) => (
  <Box sx={{
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    pb: { xs: 3, sm: 0 },
    gap: { xs: 0, sm: 3 },
    '& .MuiToolbar-root': {
      padding: '0',
      border: 'none !important',
      flex: 1
    },
    '& .MuiInputBase-root': {
      border: '1px solid #71767A !important',
    },            
    '& .MuiInputBase-root:hover': {
      border: '1px solid #068DF1 !important',
      boxShadow: '0 0 0 1px #068DF1'
    },
        
    '& .MuiInputBase-root.Mui-focused': {
      border: '1px solid #068DF1 !important',
      boxShadow: '0 0 0 1px #068DF1'
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '& .MuiFormControl-root' : {
      flex: 1,
      paddingLeft: '0 !important'
    },
    '& .MuiToolbar-root .MuiInputBase-input::placeholder': {
      color: '#111',
      fontFamily: 'Noto Sans',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '150%',
      letterSpacing: '0',
      opacity: 1
    },          
    '& .MuiToolbar-root .MuiInputBase-input': {
      color: '#111',
      fontFamily: 'Noto Sans',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '150%',
      letterSpacing: '0',
    },
    '& .MuiToolbar-root > .MuiBox-root': {
      flex: '0 0 0 !important',
      overflow: 'hidden',
      minWidth: 0,
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: '12px',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-adornedStart': {
      paddingLeft: '16px',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
      paddingRight: '16px',
    },
  }}>
    <MTableToolbar
      {...props}
      style={{ flex: 1, minWidth: 0 }}
    />
    <Button
      sx={{
        height: '44px',
        borderRadius: '24px',
        border: '1px solid #2F54A5',
        color: '#2F54A5',
        backgroundColor: '#FFF',
        backgroundClip: 'border-box',
        boxSizing: 'border-box',
        padding: '0px 16px',
        gap: '0.5rem',
        '&:hover': {
          backgroundColor: '#D8E6FF',
          color: '#001A47',
          border: '1px solid #001A47',
        },
        '&.Mui-disabled': {
          borderColor: '#D9D9D9',
          backgroundColor: '#F5F5F5',
          '& .MuiTypography-root': {
            color: '#A6A6A6'
          }
        },
        '&.Mui-disabled:hover': {
          borderColor: '#D9D9D9',
          backgroundColor: '#F5F5F5'
        }
      }} 
      onClick={() => handleDownloadCSV(props.columns, props.dataManager.sortedData)}
      disabled={!props.dataManager.sortedData.length}
    >
      <IconDownload size={20} />
      <Typography sx={{
        fontFamily: 'Noto Sans',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        textTransform: 'none',
        textAlign: 'center',
      }}>
        Download CSV
      </Typography>
    </Button>
  </Box>
);

const TenantRefreshDashboard = () => {
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState();
  const [orderDirection, setOrderDirection] = useState();
  const [sortStates, setSortStates] = useState({});
  const ALL_COLUMNS_TYPE = 'all_columns';

  /**
   * This will disable tenant dropdown and report search input in the bstBar.js
   * Do not play with AppActionTypes.SHOW_BST_BAR_TENANT
   */
  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.NONE
    });
  }, []);

  useEffect(() => {
    const fetchTenantRefreshReports = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${AppConfig.FO_INITIAL_URL}/tenant-refresh/reports`);
        setDisplayData(data); // Initialize displayData with the fetched data
      } catch (error) {
        console.error('Error fetching tenant refresh reports:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTenantRefreshReports();
  }, []);

  return (
    <MaterialTable
      title=""
      columns={RefreshDashboardColumns()}
      data={displayData} // Use displayData as the result to show
      isLoading={loading}
      orderBy={orderBy}
      orderDirection={orderDirection}
      onOrderChange={(newOrderBy) => {
        const currentState = sortStates[newOrderBy];
        let newDirection;
        if (currentState === undefined) {
          newDirection = 'asc';
        } else if (currentState === 'asc') {
          newDirection = 'desc';
        } else if (currentState === 'desc') {
          newDirection = null;
        } else {
          newDirection = 'asc';
        }
        setSortStates(prev => ({ ...prev, [newOrderBy]: newDirection }));
        setOrderBy(newOrderBy);
        setOrderDirection(newDirection);
      }}
      icons={{
        Search: React.forwardRef((props, ref) => <IconSearch {...props} ref={ref} size={20} style={{ color: '#001A47' }} />),
        SortArrow: React.forwardRef((props, ref) => <IconArrowNarrowDown {...props} ref={ref} size={16} style={{ color: '#000' }} />),
        ResetSearch: React.forwardRef(() => null),
      }}
      options={{
        maxColumnSort: ALL_COLUMNS_TYPE,
        draggable: false,
        search: true,
        searchFieldAlignment: 'left',
        searchFieldVariant: 'outlined',
        searchFieldStyle: {
          width: '100%',
          height: '44px',
          borderRadius: '16px',
          margin: '24px 0',
          border: '1px solid #71767A !important',
        },
        pageSize: 10,
        pageSizeOptions: [10, 20, 50],
        toolbar: true, // Enable toolbar for search and filter
        headerStyle: { 
          backgroundColor: '#D8E6FF',
          height: '3.5rem',
          whiteSpace: 'nowrap',
          textTransform: 'none',
          padding: '1.5rem 1rem',
          color: '#000',
          fontFamily: 'Rubik',
          fontSize: '0.875rem',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '1.3125rem',
          letterSpacing: '0.00875rem',
        },
        rowStyle: (_, index) => ({
          backgroundColor: index % 2 === 0 ? '#FFF' : '#f5f5f5',
          color: '#000',
          fontFamily: 'Noto Sans',
          fontStyle: 'normal',
          fontSize: '0.875rem',
          fontWeight: '400',
          lineHeight: '1.3125rem',
          padding: '1.5rem 1rem',
          height: '5rem'
        }),
      }}
      components={{
        Header: CustomHeader,
        Container: TableContainer,
        Toolbar: TableToolBar,
        Pagination: CustomPagination
      }}
    />
  );
};

export default TenantRefreshDashboard;
